import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Card,
  Box,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const MONTHS = [
  "January", "February", "March", "April",
  "May", "June", "July", "August",
  "September", "October", "November", "December",
];

// 4 cols × 3 rows
const CALENDAR_ROWS = [
  [0, 1, 2, 3],
  [4, 5, 6, 7],
  [8, 9, 10, 11],
];

// Same blue palette as Publications
const BLUE_GRADIENT = "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)";
const BLUE_GRADIENT_CURRENT = "linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)";

const currentYear = dayjs().year();
const YEAR_OPTIONS = Array.from(
  { length: currentYear - 2010 + 6 },   // 2010 … currentYear+5
  (_, i) => 2010 + i
);

function WorkPlanList() {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isLoading, setIsLoading] = useState(false);
  const [workPlanMap, setWorkPlanMap] = useState({});
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewRaw, setPreviewRaw] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({ queryKey: ["me"], queryFn: me, retry: false });
  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  const fetchWorkPlans = async (year) => {
    if (!meData) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: 1, limit: 12,
        from_month: `${year}-01`,
        to_month: `${year}-12`,
      });
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/work-plans/all?${params.toString()}`,
        { withCredentials: true }
      );
      const map = {};
      (response.data.data || []).forEach((wp) => { map[wp.month] = wp; });
      setWorkPlanMap(map);
    } catch {
      setSnackbar({ open: true, message: "Failed to fetch work plans.", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (meData) fetchWorkPlans(selectedYear);
  }, [meData, selectedYear]);

  const today = dayjs();

  const getMonthKey = (year, monthIndex) =>
    `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  const isWordFile = (url = "") => {
    const u = url.split("?")[0].toLowerCase();
    return u.endsWith(".doc") || u.endsWith(".docx");
  };
  const isPdfFile = (url = "") => url.split("?")[0].toLowerCase().endsWith(".pdf");

  const handlePreview = (wp, monthLabel) => {
    const url = wp.workplan_file;
    if (!url) return;
    setPreviewRaw(url);
    setPreviewTitle(`Work Plan — ${monthLabel}`);
    setPreviewUrl(isWordFile(url)
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
      : url
    );
    setPreviewOpen(true);
  };

  const closePreview = () => { setPreviewOpen(false); setPreviewUrl(null); setPreviewRaw(null); };

  const handleDownload = async (url, label) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = url.split("/").pop().split("?")[0] || `WorkPlan-${label}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: "Download started!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Download failed.", severity: "error" });
    }
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} />

      {/* Top bar */}
      <Card sx={{ mb: 2 }}>
        <MDBox
          px={3} py={2}
          display="flex" alignItems="center" justifyContent="space-between"
          flexWrap="wrap" gap={2}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <CalendarMonthIcon sx={{ color: "#1976d2", fontSize: 28 }} />
            <MDBox>
              <MDTypography variant="h5" fontWeight="medium">Work Plan</MDTypography>
              <MDTypography variant="caption" color="text">Month-wise work plan documents</MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {/* Year selector / DatePicker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["year"]}
                label="Select Year"
                value={dayjs().year(selectedYear)}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedYear(newValue.year());
                  }
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: "130px", backgroundColor: "#fff" },
                    InputProps: { readOnly: true },
                  },
                }}
              />
            </LocalizationProvider>

            {isAdmin && (
              <MDButton
                variant="gradient"
                color="info"
                startIcon={<AddCircleIcon />}
                onClick={() => navigate("/work-plan/add")}
                sx={{ borderRadius: 2, ml: "auto" }}
              >
                Upload Work Plan
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </Card>

      {/* Calendar grid */}
      {isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress color="info" />
        </MDBox>
      ) : (
        <Card sx={{ overflow: "hidden", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
          {CALENDAR_ROWS.map((row, rowIdx) => (
            <Box
              key={rowIdx}
              sx={{
                display: "grid",
                // Responsive: 1 col on xs, 2 on sm, 4 on md+
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                borderBottom: rowIdx < CALENDAR_ROWS.length - 1 ? "2px solid #e3f2fd" : "none",
              }}
            >
              {row.map((monthIdx, colIdx) => {
                const monthKey = getMonthKey(selectedYear, monthIdx);
                const wp = workPlanMap[monthKey];
                const hasData = Boolean(wp);
                const isCurrentMonth = today.format("YYYY-MM") === monthKey;

                return (
                  <Box
                    key={monthKey}
                    sx={{
                      borderRight: {
                        xs: "none",
                        sm: colIdx % 2 === 0 ? "2px solid #e3f2fd" : "none",
                        md: colIdx < 3 ? "2px solid #e3f2fd" : "none",
                      },
                      borderBottom: {
                        xs: "2px solid #e3f2fd",
                        sm: colIdx < 2 ? "none" : "2px solid #e3f2fd",
                        md: "none",
                      },
                      minHeight: { xs: "130px", md: "160px" },
                      display: "flex",
                      flexDirection: "column",
                      backgroundColor: isCurrentMonth ? "#e3f2fd" : hasData ? "#f8fbff" : "#fff",
                    }}
                  >
                    {/* Month header bar */}
                    <Box
                      sx={{
                        px: 2,
                        py: 0.9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: isCurrentMonth ? BLUE_GRADIENT_CURRENT : BLUE_GRADIENT,
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#fff", fontSize: { xs: "0.75rem", md: "0.82rem" }, letterSpacing: 0.4 }}
                      >
                        {MONTHS[monthIdx].toUpperCase()}
                      </MDTypography>

                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        {isCurrentMonth && (
                          <Chip
                            label="Current"
                            size="small"
                            sx={{ backgroundColor: "#fb8c00", color: "#fff", fontSize: "0.6rem", height: 16 }}
                          />
                        )}
                        {isAdmin && hasData && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); navigate(`/work-plan/edit/${wp.id}`); }}
                              sx={{ color: "#fff", p: 0.2 }}
                            >
                              <EditIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </MDBox>
                    </Box>

                    {/* Cell body */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 2,
                        px: 1,
                        cursor: hasData ? "pointer" : "default",
                        transition: "background 0.15s",
                        "&:hover": hasData ? { backgroundColor: "#e3f2fd" } : {},
                      }}
                      onClick={() => hasData && handlePreview(wp, `${MONTHS[monthIdx]} ${selectedYear}`)}
                    >
                      {hasData ? (
                        <>
                          {/* File icon — big, like social icons in the reference image */}
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: isPdfFile(wp.workplan_file) ? "#ffebee" : "#e3f2fd",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 0.8,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                              transition: "transform 0.15s, box-shadow 0.15s",
                              "&:hover": {
                                transform: "scale(1.12)",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                              },
                            }}
                          >
                            {isPdfFile(wp.workplan_file) ? (
                              <PictureAsPdfIcon sx={{ fontSize: { xs: 26, md: 32 }, color: "#e53935" }} />
                            ) : (
                              <ArticleIcon sx={{ fontSize: { xs: 26, md: 32 }, color: "#1565c0" }} />
                            )}
                          </Box>

                          <MDTypography
                            variant="caption"
                            sx={{ color: "#1976d2", fontWeight: "bold", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.5 }}
                          >
                            {isPdfFile(wp.workplan_file) ? "PDF" : "Word"}
                          </MDTypography>

                          {/* "Click to view" in a distinct teal/orange accent */}
                          <MDTypography
                            variant="caption"
                            sx={{
                              mt: 0.4,
                              fontSize: "0.63rem",
                              fontWeight: "medium",
                              color: "#e65100",
                              letterSpacing: 0.3,
                            }}
                          >
                            Click to view
                          </MDTypography>
                        </>
                      ) : (
                        <>
                          <EventBusyIcon sx={{ fontSize: 24, color: "#9e9e9e", mb: 0.5 }} />
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#424242", fontSize: "0.68rem", fontWeight: "bold", textAlign: "center" }}
                          >
                            No Work Plan Added
                          </MDTypography>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            {previewRaw && isPdfFile(previewRaw)
              ? <PictureAsPdfIcon sx={{ color: "#e53935", fontSize: 22 }} />
              : <ArticleIcon sx={{ color: "#1565c0", fontSize: 22 }} />}
            <MDTypography variant="h6" fontWeight="bold">{previewTitle}</MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} alignItems="center">
            <MDButton
              size="small" variant="gradient" color="info"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(previewRaw, previewTitle)}
            >
              Download
            </MDButton>
            <IconButton onClick={closePreview} size="small"><CloseIcon /></IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title={previewTitle}
              width="100%"
              height="100%"
              style={{ border: "none", display: "block", minHeight: "70vh" }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <MDButton variant="outlined" color="dark" onClick={closePreview} size="small">Close</MDButton>
          <MDButton
            variant="gradient" color="info"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(previewRaw, previewTitle)}
            size="small"
          >
            Download
          </MDButton>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default WorkPlanList;
